import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../utils/config';
import { matrisiUnut } from '../utils/yetkiMatris';
import oturumSaati from '../utils/oturumSaati';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BAGLANTI_HATASI = 'Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edip tekrar deneyin.';

/** Giriş uçlarının yanıtını tek biçime çevirir: hata alanları (robot_gerekli, kilit_kalan …) olduğu gibi taşınır. */
const yanitiOku = async (response) => {
  let veri = {};
  try {
    veri = await response.json();
  } catch (e) {
    veri = { detail: `Sunucu hatası: ${response.status}` };
  }
  if (response.ok) return { ok: true, ...veri };
  return { ok: false, status: response.status, ...veri, error: typeof veri.detail === 'string' ? veri.detail : 'İşlem başarısız oldu.' };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = SHARED_API_BASE_URL;

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        await verifyToken(storedToken);
        try {
          await fetch(`${API_BASE_URL}/feed/favorites/init-table`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${storedToken}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
        }
      } else {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Oturum sunucuda bitti (hareketsizlik, 24 saat, BT kapattı, şifre değişti): utils/apiAuth.js 401'i yakalar,
  // mesajı giriş ekranı için saklar ve bu olayı yayınlar.
  useEffect(() => {
    const bitti = () => yerelCikis();
    window.addEventListener('oturum-bitti', bitti);
    return () => window.removeEventListener('oturum-bitti', bitti);
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      if (tokenToVerify && tokenToVerify.startsWith('admin_static_token_')) {
        const storedUser = localStorage.getItem('auth_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (userData.username === 'admin' && userData.isAdmin) {
            setUser(userData);
            setToken(tokenToVerify);
            setLoading(false);
            return;
          }
        }
        logout();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/login/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setToken(tokenToVerify);
      } else if (response.status === 401) {
        // Sadece geçersiz/expired token (401) çıkışa yol açar; sebep mesajını apiAuth giriş ekranına taşır.
        console.warn('Token geçersiz (401), kullanıcı çıkış yapılıyor');
        yerelCikis();
      } else {
        // 403 (yetki yok) dahil diğer durumlarda kullanıcı login'e yönlendirilmez.
        console.warn('Token doğrulama 401 değil, kullanıcı giriş yapılı kalacak:', response.status);
      }
    } catch (error) {
      console.error('Token verification network error (kullanıcı giriş yapılı kalacak):', error);
    } finally {
      setLoading(false);
    }
  };

  const girisiTamamla = (data) => {
    const { access_token, user: userData } = data;
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    if (data.oturum) oturumSaati.guncelle(data.oturum.kalan, data.oturum.tur);
    setToken(access_token);
    setUser(userData);
    return { success: true, user: userData };
  };

  const istek = async (yol, secenekler) => {
    try {
      return await yanitiOku(await fetch(`${API_BASE_URL}${yol}`, secenekler));
    } catch (error) {
      console.error('Giriş isteği hatası:', error);
      return { ok: false, status: 0, error: BAGLANTI_HATASI };
    }
  };

  /**
   * 1. adım. Dönüş: {success, user} (iki adım gerekmiyorsa) | {ikiAdim: true, on_oturum, telefon, …}
   * | {success: false, error, status, robot_gerekli?, kilit_kalan?, kalan_hak?}
   */
  const login = async (username, password, altcha) => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    if (altcha) params.append('altcha', altcha);
    const sonuc = await istek('/login/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    if (!sonuc.ok) return { ...sonuc, success: false };
    if (sonuc.iki_adim) return { ...sonuc, ikiAdim: true };
    return girisiTamamla(sonuc);
  };

  /** 2. adım: SMS kodu. Dönüş login ile aynı biçimde. */
  const kodDogrula = async (onOturum, kod, altcha) => {
    const sonuc = await istek('/login/dogrula', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ on_oturum: onOturum, kod, altcha: altcha || null }),
    });
    if (!sonuc.ok) return { ...sonuc, success: false };
    return girisiTamamla(sonuc);
  };

  const kodTekrar = async (onOturum) => {
    const sonuc = await istek('/login/kod-tekrar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ on_oturum: onOturum }),
    });
    return { ...sonuc, success: sonuc.ok };
  };

  /** Sunucuya gitmeden yerel temizlik (oturum zaten sunucuda bitmişse). */
  const yerelCikis = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    oturumSaati.temizle();
    setToken(null);
    setUser(null);
    // Yetki matrisi kullanıcıya özel izinleri içeriyor; oturum kapanınca
    // önbellek temizlenmezse sonraki kullanıcı öncekinin izinlerini görür.
    matrisiUnut();
  };

  const logout = () => {
    // Oturum sunucuda da kapanır: token 24 saat dolmadan başkasının eline geçse bile işe yaramaz.
    const mevcut = localStorage.getItem('auth_token');
    if (mevcut) {
      fetch(`${API_BASE_URL}/login/cikis`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${mevcut}` },
        keepalive: true,
      }).catch(() => {});
    }
    yerelCikis();
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const value = {
    user,
    token,
    loading,
    login,
    kodDogrula,
    kodTekrar,
    logout,
    isAuthenticated,
    getAuthHeaders,
    verifyToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
